<template>
  <div class="gx-sb-detail">
    <header class="gx-sb-detail__header">
      <button type="button" class="gx-sb-detail__back" @click="goBack">
        <span class="gx-sb-detail__back-icon">‹</span>
        返回
      </button>
      <div class="divider-line"></div>
      <h1>{{ pageTitle }}</h1>
    </header>

    <div class="gx-sb-detail__modebar">
      <nav class="gx-sb-detail__modes" aria-label="店铺分析范围">
        <button
          v-for="mode in modeTabs"
          :key="mode.key"
          type="button"
          class="gx-sb-detail__mode"
          :class="{ 'gx-sb-detail__mode--active': activeMode === mode.key }"
          @click="selectMode(mode.key)"
        >{{ mode.label }}</button>
      </nav>
      <label class="gx-sb-detail__scope">
        <span>{{ selectorLabel }}</span>
        <el-select
          v-if="activeMode === 'multiShop'"
          v-model="selectedShopIds"
          multiple
          collapse-tags
          size="small"
          class="gx-sb-detail__shop-multi-select"
          placeholder="请选择店铺"
        >
          <el-option v-for="option in shopOptions" :key="option.value" :label="option.label" :value="option.value" />
        </el-select>
        <el-select
          v-else-if="activeMode === 'multiGroup'"
          v-model="selectedGroupIds"
          multiple
          collapse-tags
          size="small"
          class="gx-sb-detail__group-multi-select"
          placeholder="请选择分组"
        >
          <el-option v-for="option in multiGroupOptions" :key="option.value" :label="option.label" :value="option.value" />
        </el-select>
        <select v-else v-model="selectorValue">
          <option v-for="option in selectorOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </label>
    </div>

    <main class="gx-sb-detail__content">
      <!--店铺诊断-->
      <section class="gx-sb-detail__panel">
        <div class="gx-sb-detail__panel-head">
          <div class="gx-sb-detail__panel-title">
            <span class="gx-sb-detail__title-bar"></span>
            店铺诊断
          </div>
          <div class="gx-sb-detail__panel-actions">
            <button
              v-for="date in dateOptions"
              :key="date.key"
              type="button"
              class="gx-sb-detail__date"
              :class="{ 'gx-sb-detail__date--active': dateKey === date.key }"
              @click="selectDate(date.key)"
            >{{ date.label }}</button>
            <span class="gx-sb-detail__date-range">{{ dateRangeText }}</span>
          </div>
          <svg @click="diagnosisExpanded = !diagnosisExpanded" width="14" height="14" viewBox="0 0 14 14" fill="none" :style="`transform: rotate(${diagnosisExpanded?180:0}deg); transition: transform 0.2s; flex-shrink: 0; margin-left: 8px;`">
            <path d="M3 5l4 4 4-4" stroke="#999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </div>

        <div v-show="diagnosisExpanded" class="gx-sb-detail__panel-body">
          <!--单店-店铺诊断-->
          <template v-if="activeMode === 'single'">
            <div class="gx-sb-detail__subhead">商品分类总览</div>
            <div class="gx-sb-detail__status-grid">
              <article
                v-for="card in categoryCards"
                :key="card.key"
                class="gx-sb-detail__status-card"
                :style="{ borderTopColor: card.color }"
              >
                <div class="gx-sb-detail__status-name" :style="{ color: card.color }">{{ card.label }}</div>
                <div class="gx-sb-detail__status-desc">{{ card.desc }}</div>
                <div class="gx-sb-detail__status-value">{{ card.count }}款</div>
                <div class="gx-sb-detail__status-amount">{{ card.amount }}</div>
                <div class="gx-sb-detail__status-foot">
                  <span>占比{{ card.percent }}</span>
                  <span :class="changeClass(card.change)">{{ card.change }}</span>
                </div>
              </article>
            </div>

            <div class="gx-sb-detail__subhead gx-sb-detail__subhead--table">分类数据明细</div>
            <div class="gx-sb-detail__table-wrap">
              <table class="gx-sb-detail__table gx-sb-detail__table--category">
                <thead>
                  <tr>
                    <th v-for="column in categoryColumns" :key="column.key">{{ column.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in categoryRows" :key="row.label" :class="{ 'is-total': row.total }">
                    <td>{{ row.label }}</td>
                    <td>{{ row.count }}</td>
                    <td>{{ row.exposure }}</td>
                    <td>{{ row.click }}</td>
                    <td>{{ row.clickRate }}</td>
                    <td>{{ row.cart }}</td>
                    <td>{{ row.cartRate }}</td>
                    <td>{{ row.payRate }}</td>
                    <td>{{ row.sales }}</td>
                    <td>{{ row.avgPrice }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="gx-sb-detail__subhead gx-sb-detail__subhead--table">分类均值对比（单品均值）</div>
            <div class="gx-sb-detail__average-grid">
              <article
                v-for="card in averageCards"
                :key="card.key"
                class="gx-sb-detail__average-card"
                :style="{ borderTopColor: card.color }"
              >
                <div class="gx-sb-detail__average-title" :style="{ color: card.color }">{{ card.label }}</div>
                <div v-for="metric in card.metrics" :key="metric.label" class="gx-sb-detail__average-row">
                  <span>{{ metric.label }}</span>
                  <strong>{{ metric.value }}</strong>
                </div>
              </article>
            </div>

            <div class="gx-sb-detail__subhead gx-sb-detail__subhead--target">目标完成分析</div>
            <div class="gx-sb-detail__target-input">
              <span>目标营业额</span>
              <input v-model.number="targetAmount" type="number" min="0" aria-label="目标营业额">
            </div>
            <div class="gx-sb-detail__table-wrap">
              <table class="gx-sb-detail__table gx-sb-detail__table--target">
                <thead>
                  <tr>
                    <th v-for="column in targetColumns" :key="column.key">{{ column.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in targetRows" :key="row.label">
                    <td>{{ row.label }}</td>
                    <td>{{ row.percent }}</td>
                    <td class="text-blue">{{ row.current }}</td>
                    <td class="text-red">{{ row.need }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="gx-sb-detail__hint">全店35款，任一商品少量即可完成目标，当前展示为虚拟数据。</p>

            <div class="gx-sb-detail__subhead gx-sb-detail__subhead--action">近期优化动作追踪</div>
            <div v-for="action in optimizationActions" :key="action.date" class="gx-sb-detail__action-card">
              <div class="gx-sb-detail__action-meta">
                <span>{{ action.date }}</span>
                <strong>{{ action.title }}</strong>
                <span>{{ action.note }}</span>
              </div>
              <div class="gx-sb-detail__action-columns">
                <div>
                  <span>动作前</span>
                  <strong>{{ action.beforeFirst }}</strong>
                  <strong>{{ action.beforeSecond }}</strong>
                </div>
                <span class="gx-sb-detail__action-arrow" aria-hidden="true">→</span>
                <div>
                  <span>动作后</span>
                  <strong>{{ action.afterFirst }}</strong>
                  <strong>{{ action.afterSecond }}</strong>
                </div>
                <div class="is-positive">
                  <span>变化</span>
                  <strong>{{ action.changeFirst }}</strong>
                  <strong>{{ action.changeSecond }}</strong>
                </div>
              </div>
            </div>

            <div class="gx-sb-detail__subhead gx-sb-detail__subhead--conclusion">诊断结论</div>
            <div class="gx-sb-detail__conclusion">{{ diagnosisConclusion }}</div>

            <div class="gx-sb-detail__subhead gx-sb-detail__subhead--revenue">营业额提升建议</div>
            <div class="gx-sb-detail__revenue-list">
              <div v-for="item in revenueSuggestions" :key="item.label" class="gx-sb-detail__revenue-item">
                <span class="gx-sb-detail__revenue-icon" :class="`is-${item.tone}`" aria-hidden="true">{{ item.icon }}</span>
                <span>{{ item.label }}：{{ item.text }}</span>
              </div>
            </div>
          </template>
          <!--分组-店铺诊断-->
          <template v-else-if="activeMode === 'group'">
            <div class="gx-sb-detail__subhead">{{ groupDiagnosisScope }}汇总概览</div>
            <div class="gx-sb-detail__group-summary-grid">
              <article v-for="card in groupSummaryCards" :key="card.key" class="gx-sb-detail__group-summary-card">
                <span>{{ card.label }}</span>
                <strong>{{ card.value }}</strong>
                <small>{{ card.note }}</small>
              </article>
            </div>
            <div class="gx-sb-detail__table-wrap gx-sb-detail__group-diagnosis-table">
              <table class="gx-sb-detail__table gx-sb-detail__table--comparison">
                <thead>
                  <tr>
                    <th v-for="column in groupShopColumns" :key="column.key">{{ column.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in groupShopRows" :key="row.shop" :class="{ 'is-total': row.total }">
                    <td><a v-if="!row.total" href="javascript:;" class="gx-sb-detail__link">{{ row.shop }}</a><span v-else>{{ row.shop }}</span></td>
                    <td>{{ row.amount }}</td>
                    <td :class="changeClass(row.trend)">{{ row.trend }}</td>
                    <td class="text-red">{{ row.hot }}</td>
                    <td class="text-blue">{{ row.moving }}</td>
                    <td class="text-green">{{ row.flat }}</td>
                    <td class="text-orange">{{ row.scattered }}</td>
                    <td class="text-purple">{{ row.cart }}</td>
                    <td class="text-gray">{{ row.noData }}</td>
                    <td>{{ row.action }}</td>
                    <td><span class="gx-sb-detail__rating" :class="ratingClass(row.rating)">{{ row.rating }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="gx-sb-detail__group-conclusion">{{ groupDiagnosisConclusion }}</div>
            <div class="gx-sb-detail__group-actions">
              <div v-for="(item, index) in groupDiagnosisActions" :key="item" class="gx-sb-detail__group-action">
                <span>{{ index + 1 }}</span>{{ item }}
              </div>
            </div>
          </template>
          <template v-else>
          <div v-if="activeMode === 'multiGroup'" class="gx-sb-detail__table-wrap">
            <table class="gx-sb-detail__table gx-sb-detail__table--comparison">
              <thead>
                <tr>
                  <th v-for="column in groupColumns" :key="column.key">{{ column.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in displayGroupRows" :key="row.label" :class="{ 'is-total': row.total }">
                  <td><a v-if="!row.total" href="javascript:;" class="gx-sb-detail__link">{{ row.label }}</a><span v-else>{{ row.label }}</span></td>
                  <td>{{ row.amount }}</td>
                  <td :class="changeClass(row.trend)">{{ row.trend }}</td>
                  <td class="text-red">{{ row.hot }}</td>
                  <td class="text-blue">{{ row.moving }}</td>
                  <td class="text-green">{{ row.flat }}</td>
                  <td class="text-orange">{{ row.scattered }}</td>
                  <td class="text-purple">{{ row.cart }}</td>
                  <td class="text-gray">{{ row.noData }}</td>
                  <td>{{ row.action }}</td>
                  <td><span class="gx-sb-detail__rating" :class="ratingClass(row.rating)">{{ row.rating }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <template v-if="activeMode === 'multiShop'">
            <div class="gx-sb-detail__table-wrap">
              <table class="gx-sb-detail__table gx-sb-detail__table--comparison">
                <thead>
                  <tr>
                    <th v-for="column in multiShopColumns" :key="column.key">{{ column.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in filteredMultiShopDiagnosisRows" :key="row.shop" :class="{ 'is-total': row.total }">
                    <td><a v-if="!row.total" href="javascript:;" class="gx-sb-detail__link">{{ row.shop }}</a><span v-else>{{ row.shop }}</span></td>
                    <td>{{ row.group }}</td>
                    <td>{{ row.amount }}</td>
                    <td :class="changeClass(row.trend)">{{ row.trend }}</td>
                    <td class="text-red">{{ row.hot }}</td>
                    <td class="text-blue">{{ row.moving }}</td>
                    <td class="text-green">{{ row.flat }}</td>
                    <td class="text-orange">{{ row.scattered }}</td>
                    <td class="text-purple">{{ row.cart }}</td>
                    <td class="text-gray">{{ row.noData }}</td>
                    <td>{{ row.action }}</td>
                    <td><span class="gx-sb-detail__rating" :class="ratingClass(row.rating)">{{ row.rating }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
          </template>
        </div>
      </section>
      <!--多店对比-->
      <section class="gx-sb-detail__panel gx-sb-detail__compare-panel">
        <div class="gx-sb-detail__panel-head">
          <div class="gx-sb-detail__panel-title">
            <span class="gx-sb-detail__title-bar gx-sb-detail__title-bar--blue"></span>
            多店对比
          </div>
          <div class="gx-sb-detail__panel-actions">
            <button
              v-for="date in dateOptions"
              :key="`compare-${date.key}`"
              type="button"
              class="gx-sb-detail__date"
              :class="{ 'gx-sb-detail__date--active': compareDateKey === date.key }"
              @click="selectCompareDate(date.key)"
            >{{ date.label }}</button>
            <span class="gx-sb-detail__date-range">{{ compareDateRangeText }}</span>
          </div>
          <svg @click="multiShopExpanded = !multiShopExpanded" width="14" height="14" viewBox="0 0 14 14" fill="none" :style="`transform: rotate(${multiShopExpanded?180:0}deg); transition: transform 0.2s; flex-shrink: 0; margin-left: 8px;`">
            <path d="M3 5l4 4 4-4" stroke="#999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </div>
        <div v-show="multiShopExpanded" class="gx-sb-detail__panel-body">
          <!--多分组-多店对比-->
          <template v-if="activeMode === 'multiGroup'">
            <div class="gx-sb-detail__table-wrap">
              <table class="gx-sb-detail__table gx-sb-detail__table--comparison">
                <thead>
                  <tr>
                    <th v-for="column in multiShopColumns" :key="column.key">{{ column.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(row, index) in filteredMultiGroupRows">
                    <tr v-if="row.groupHeader" :key="`group-${index}`" class="is-group">
                      <td :colspan="multiShopColumns.length">{{ row.groupHeader }}</td>
                    </tr>
                    <tr v-else :key="`shop-${index}`" :class="{ 'is-total': row.total }">
                      <td><a v-if="!row.total" href="javascript:;" class="gx-sb-detail__link">{{ row.shop }}</a><span v-else>{{ row.shop }}</span></td>
                      <td>{{ row.group }}</td>
                      <td>{{ row.amount }}</td>
                      <td :class="changeClass(row.trend)">{{ row.trend }}</td>
                      <td class="text-red">{{ row.hot }}</td>
                      <td class="text-blue">{{ row.moving }}</td>
                      <td class="text-green">{{ row.flat }}</td>
                      <td class="text-orange">{{ row.scattered }}</td>
                      <td class="text-purple">{{ row.cart }}</td>
                      <td class="text-gray">{{ row.noData }}</td>
                      <td>{{ row.action }}</td>
                      <td><span class="gx-sb-detail__rating" :class="ratingClass(row.rating)">{{ row.rating }}</span></td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
            <div class="gx-sb-detail__subhead gx-sb-detail__subhead--table">差店铺改善建议</div>
            <article v-for="suggestion in suggestions" :key="suggestion.shop" class="gx-sb-detail__suggestion">
              <strong>{{ suggestion.shop }}</strong>
              <p>{{ suggestion.summary }}</p>
              <ol>
                <li v-for="item in suggestion.items" :key="item">{{ item }}</li>
              </ol>
              <b>{{ suggestion.result }}</b>
            </article>
          </template>
          <template v-else>
            <div class="gx-sb-detail__table-wrap">
              <table class="gx-sb-detail__table gx-sb-detail__table--comparison">
                <thead>
                  <tr>
                    <th v-for="column in multiShopColumns" :key="column.key">{{ column.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in multiShopCompareRows" :key="row.shop" :class="{ 'is-total': row.total }">
                    <td><a v-if="!row.total" href="javascript:;" class="gx-sb-detail__link">{{ row.shop }}</a><span v-else>{{ row.shop }}</span></td>
                    <td>{{ row.group }}</td>
                    <td>{{ row.amount }}</td>
                    <td :class="changeClass(row.trend)">{{ row.trend }}</td>
                    <td class="text-red">{{ row.hot }}</td>
                    <td class="text-blue">{{ row.moving }}</td>
                    <td class="text-green">{{ row.flat }}</td>
                    <td class="text-orange">{{ row.scattered }}</td>
                    <td class="text-purple">{{ row.cart }}</td>
                    <td class="text-gray">{{ row.noData }}</td>
                    <td>{{ row.action }}</td>
                    <td><span class="gx-sb-detail__rating" :class="ratingClass(row.rating)">{{ row.rating }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="gx-sb-detail__subhead gx-sb-detail__subhead--table">差店铺改善建议</div>
            <div class="gx-sb-detail__suggestion-grid">
              <article v-for="suggestion in compareSuggestions" :key="suggestion.shop" class="gx-sb-detail__suggestion">
                <strong>{{ suggestion.shop }}</strong>
                <p>{{ suggestion.summary }}</p>
                <ol>
                  <li v-for="item in suggestion.items" :key="item">{{ item }}</li>
                </ol>
                <b>{{ suggestion.result }}</b>
              </article>
            </div>
          </template>
        </div>
      </section>
      <!--成交数据-->
      <section class="gx-sb-detail__panel">
        <div class="gx-sb-detail__panel-head">
          <div class="gx-sb-detail__panel-title">
            <span class="gx-sb-detail__title-bar gx-sb-detail__title-bar--blue"></span>
            成交数据
          </div>
          <div class="gx-sb-detail__panel-actions">
            <button
              v-for="date in dateOptions"
              :key="`transaction-${date.key}`"
              type="button"
              class="gx-sb-detail__date"
              :class="{ 'gx-sb-detail__date--active': dateKey === date.key }"
              @click="selectDate(date.key)"
            >{{ date.label }}</button>
            <span class="gx-sb-detail__date-range">{{ dateRangeText }}</span>
          </div>
          <svg @click="transactionExpanded = !transactionExpanded" width="14" height="14" viewBox="0 0 14 14" fill="none" :style="`transform: rotate(${transactionExpanded?180:0}deg); transition: transform 0.2s; flex-shrink: 0; margin-left: 8px;`">
            <path d="M3 5l4 4 4-4" stroke="#999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </div>

        <div v-show="transactionExpanded" class="gx-sb-detail__transaction">
          <div class="gx-sb-detail__transaction-tabs">
            <button
              v-for="tab in transactionTabs"
              :key="tab.key"
              type="button"
              :class="{ 'is-active': activeTransactionTab === tab.key }"
              @click="activeTransactionTab = tab.key"
            >{{ tab.label }}</button>
          </div>
          <div class="gx-sb-detail__transaction-box">
            <!--成交数据-成交概览-->
            <template v-if="activeTransactionTab === 'overview'">
              <div class="gx-sb-detail__overview-toolbar">
                <span>售卖类型：</span>
                <button v-for="option in overviewSaleTypes" :key="option.key" type="button" :class="{ 'is-active': overviewSaleType === option.key }" @click="overviewSaleType = option.key">{{ option.label }}</button>
                <i></i>
                <span>载体：</span>
                <select v-model="overviewCarrier"><option v-for="option in overviewCarriers" :key="option.key" :value="option.key">{{ option.label }}</option></select>
                <span>投放时段：</span>
                <select v-model="overviewPeriod"><option v-for="option in overviewPeriods" :key="option.key" :value="option.key">{{ option.label }}</option></select>
              </div>
              <div class="gx-sb-detail__overview-metrics">
                <article v-for="metric in overviewMetrics" :key="metric.key" class="gx-sb-detail__overview-metric">
                  <span>{{ metric.label }}</span>
                  <strong :class="metric.tone ? `is-${metric.tone}` : ''">{{ metric.value }}</strong>
                  <small :class="metric.change.indexOf('-') > -1 ? 'is-negative' : ''">{{ metric.change }}</small>
                </article>
              </div>
              <div class="gx-sb-detail__trend-box">
                <div class="gx-sb-detail__trend-head">
                  <strong>成交趋势</strong>
                 </div>
                <div ref="trendChart" class="gx-sb-detail__trend-chart" role="img" aria-label="成交趋势折线图"></div>
              </div>
            </template>
            <template v-else-if="activeTransactionTab === 'income'">
              <div class="gx-sb-detail__income-toolbar">
                <span>售卖类型：</span>
                <button v-for="option in overviewSaleTypes" :key="`income-sale-${option.key}`" type="button" :class="{ 'is-active': overviewSaleType === option.key }" @click="overviewSaleType = option.key">{{ option.label }}</button>
                <i></i>
                <span>载体：</span>
                <select v-model="overviewCarrier"><option v-for="option in overviewCarriers" :key="`income-carrier-${option.key}`" :value="option.key">{{ option.label }}</option></select>
                <span>投放时段：</span>
                <select v-model="overviewPeriod"><option v-for="option in overviewPeriods" :key="`income-period-${option.key}`" :value="option.key">{{ option.label }}</option></select>
                <span class="gx-sb-detail__income-caliber-label">时间口径：</span>
                <button v-for="option in incomeTimeCalibers" :key="option.key" type="button" :class="{ 'is-active': incomeTimeCaliber === option.key }" @click="incomeTimeCaliber = option.key">{{ option.label }}</button>
              </div>
              <div class="gx-sb-detail__subhead gx-sb-detail__subhead--income">
                <span class="gx-sb-detail__title-bar"></span>
                收支概况
              </div>
              <div class="gx-sb-detail__income-sections">
                <article v-for="section in currentTransaction.sections" :key="section.key" class="gx-sb-detail__income-section" :class="`is-${section.tone}`">
                  <div class="gx-sb-detail__income-section-head">
                    <span class="gx-sb-detail__title-bar" :class="`gx-sb-detail__title-bar--${section.tone}`"></span>
                    <strong>{{ section.title }}</strong>
                  </div>
                  <div class="gx-sb-detail__income-formula">
                    <strong v-if="section.label">{{ section.label }} <em>{{ section.value }}</em></strong>
                    <template v-if="section.label"><b>=</b></template>
                    <template v-for="(item, index) in section.items">
                      <span v-if="index" :key="`${section.key}-plus-${index}`" class="gx-sb-detail__income-plus">+</span>
                      <span :key="`${section.key}-item-${index}`" class="gx-sb-detail__income-chip">{{ item }}</span>
                    </template>
                  </div>
                </article>
              </div>
            </template>
            <template v-else>
              <div class="gx-sb-detail__subhead gx-sb-detail__subhead--refund">
                <span class="gx-sb-detail__title-bar"></span>
                {{ currentTransaction.title }}
              </div>
              <div class="gx-table gx-sb-detail__table-wrap gx-sb-detail__table-wrap--border">
                <table class="gx-sb-detail__table gx-sb-detail__table--transaction">
                  <thead>
                    <tr>
                      <th v-for="column in currentTransaction.columns" :key="column.key">{{ column.label }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in currentTransaction.rows" :key="index">
                      <td v-for="column in currentTransaction.columns" :key="column.key" :class="column.className || ''">{{ row[column.key] }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
        </div>
      </section>
      <!-- 退款数据-->
      <section class="gx-sb-detail__panel">
        <div class="gx-sb-detail__panel-head">
          <div class="gx-sb-detail__panel-title">
            <span class="gx-sb-detail__title-bar gx-sb-detail__title-bar--orange"></span>
            退款数据
          </div>
          <svg @click="refundExpanded = !refundExpanded" width="14" height="14" viewBox="0 0 14 14" fill="none" :style="`transform: rotate(${refundExpanded?180:0}deg); transition: transform 0.2s; flex-shrink: 0; margin-left: 8px;`">
            <path d="M3 5l4 4 4-4" stroke="#999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </div>

        <div v-show="refundExpanded" class="gx-sb-detail__refund">
          <div class="gx-sb-detail__transaction-tabs gx-sb-detail__refund-tabs">
            <button
              v-for="tab in refundTabs"
              :key="tab.key"
              type="button"
              :class="{ 'is-active': activeRefundTab === tab.key }"
              @click="activeRefundTab = tab.key"
            >{{ tab.label }}</button>
          </div>
          <div class="gx-sb-detail__transaction-box">
            <div class="gx-sb-detail__refund-toolbar">
              <span>售卖类型：</span>
              <button
                v-for="type in refundSaleTypes"
                :key="type.key"
                type="button"
                :class="{ 'is-active': refundSaleType === type.key }"
                @click="refundSaleType = type.key"
              >{{ type.label }}</button>
            </div>
            <div class="gx-sb-detail__subhead gx-sb-detail__subhead--refund">
              <span class="gx-sb-detail__title-bar"></span>
              {{ currentRefund.title }}
            </div>
            <div class="gx-table gx-sb-detail__table-wrap gx-sb-detail__table-wrap--border">
              <table class="gx-sb-detail__table gx-sb-detail__table--refund">
                <thead>
                  <tr>
                    <th v-for="column in currentRefund.columns" :key="column.key">{{ column.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in filteredRefundRows" :key="`${activeRefundTab}-${index}`" :class="{ 'is-total': row.total }">
                    <td v-for="column in currentRefund.columns" :key="column.key" :class="column.className || ''">{{ row[column.key] }}</td>
                  </tr>
                  <tr v-if="!filteredRefundRows.length" class="is-empty">
                    <td :colspan="currentRefund.columns.length">暂无退款数据</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          </div>
      </section>
    </main>
    <under-development ref="underDevelopmentRef"></under-development>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import UnderDevelopment from "@/components/newUi/UnderDevelopment.vue";

const TRANSACTION_TABS = [
  { key: 'overview', label: '成交概况' },
  { key: 'income', label: '收支概况' },
  { key: 'carrier', label: '载体构成' },
  { key: 'account', label: '账号构成' },
  { key: 'terminal', label: '终端构成' },
  { key: 'category', label: '品类构成' },
  { key: 'product', label: '商品构成' },
  { key: 'price', label: '价格带构成' },
  { key: 'audience', label: '人群构成' }
]

const TRANSACTION_DATA = {
  overview: {
    title: '成交概况',
    columns: [{ key: 'metric', label: '指标' }, { key: 'today', label: '当前值' }, { key: 'previous', label: '对比值' }, { key: 'change', label: '变化' }],
    rows: [
      { metric: '用户支付金额', today: '¥25,380', previous: '¥23,100', change: '+9.87%' },
      { metric: '成交订单数', today: '35', previous: '31', change: '+12.90%' },
      { metric: '成交人数', today: '29', previous: '27', change: '+7.41%' },
      { metric: '客单价', today: '¥875.17', previous: '¥855.56', change: '+2.29%' }
    ]
  },
  income: {
    title: '收支概况',
    sections: [
      {
        key: 'deal',
        title: '成交构成',
        tone: 'red',
        label: '成交金额',
        value: '--',
        items: ['用户支付金额(-)', '智能优惠券金额(-)', '电商平台补贴金额(-)', '达人补贴金额(-)', '预售定金(-)']
      },
      {
        key: 'refund',
        title: '退款构成',
        tone: 'orange',
        label: '成功退款金额',
        value: '--',
        items: ['退款金额(-)', '智能优惠券退款(-)', '平台补贴退款(-)', '达人补贴退款(-)']
      },
      {
        key: 'expense',
        title: '支出构成',
        tone: 'blue',
        label: '支出金额',
        value: '--',
        items: ['投放消耗(-)', '平台佣金(结算口径)(-)', '达人佣金(结算口径)(-)']
      },
      {
        key: 'other',
        title: '其他指标',
        tone: 'green',
        label: '',
        value: '',
        items: ['退款率(支付时间)(-)', '投放费比(刷量数据)(-)']
      }
    ],
    columns: [{ key: 'type', label: '收支项目' }, { key: 'amount', label: '金额' }, { key: 'ratio', label: '占比' }, { key: 'change', label: '较上期' }],
    rows: [
      { type: '成交金额', amount: '¥25,380.00', ratio: '100%', change: '+9.87%' },
      { type: '平台补贴', amount: '¥1,280.00', ratio: '5.04%', change: '+4.16%' },
      { type: '退款金额', amount: '¥860.00', ratio: '3.39%', change: '-2.33%' },
      { type: '商家实际收入', amount: '¥23,640.00', ratio: '93.14%', change: '+11.08%' }
    ]
  },
  carrier: {
    title: '载体构成',
    columns: [{ key: 'type', label: '载体类型' }, { key: 'amount', label: '支付金额' }, { key: 'orders', label: '成交订单数' }, { key: 'users', label: '成交人数' }, { key: 'avg', label: '客单价' }],
    rows: [
      { type: '直播', amount: '¥12,860.00', orders: '16', users: '14', avg: '¥918.57' },
      { type: '短视频', amount: '¥7,320.00', orders: '9', users: '8', avg: '¥915.00' },
      { type: '商品卡', amount: '¥5,200.00', orders: '8', users: '7', avg: '¥742.86' }
    ]
  },
  account: {
    title: '账号构成',
    columns: [{ key: 'type', label: '账号名称' }, { key: 'carrier', label: '账号类型' }, { key: 'amount', label: '支付金额' }, { key: 'users', label: '成交人数' }, { key: 'rate', label: '点击成交率' }],
    rows: [
      { type: '观星树官方号', carrier: '自营', amount: '¥13,980.00', users: '15', rate: '5.12%' },
      { type: '男装精选达人', carrier: '带货', amount: '¥8,760.00', users: '9', rate: '4.65%' },
      { type: '潮流穿搭号', carrier: '带货', amount: '¥2,640.00', users: '5', rate: '3.18%' }
    ]
  },
  terminal: {
    title: '终端构成',
    columns: [{ key: 'type', label: '终端类型' }, { key: 'amount', label: '支付金额', className: 'text-red' }, { key: 'users', label: '成交人数' }, { key: 'avg', label: '客单价', className: 'text-red' }, { key: 'rate', label: '点击成交率' }],
    rows: [
      { type: '抖音', amount: '¥970.88', users: '4', avg: '¥3.04', rate: '0.03%' },
      { type: '抖音极速版', amount: '¥210.29', users: '1', avg: '¥1.94', rate: '0.07%' },
      { type: '整体', amount: '¥810.36', users: '0', avg: '¥0.00', rate: '0.02%' },
      { type: '其他', amount: '¥0.00', users: '0', avg: '¥0.00', rate: '0.00%' },
      { type: '红果短剧', amount: '¥0.00', users: '0', avg: '¥0.00', rate: '0.00%' }
    ]
  },
  category: {
    title: '品类构成',
    columns: [{ key: 'type', label: '商品类目' }, { key: 'amount', label: '支付金额' }, { key: 'orders', label: '成交订单数' }, { key: 'avg', label: '客单价' }, { key: 'rate', label: '点击成交率' }],
    rows: [
      { type: '男装', amount: '¥15,680.00', orders: '19', avg: '¥825.26', rate: '4.68%' },
      { type: '女装', amount: '¥6,480.00', orders: '9', avg: '¥720.00', rate: '3.95%' },
      { type: '配饰', amount: '¥3,220.00', orders: '7', avg: '¥460.00', rate: '2.86%' }
    ]
  },
  product: {
    title: '商品构成',
    columns: [{ key: 'type', label: '商品名称' }, { key: 'id', label: '商品ID' }, { key: 'amount', label: '支付金额' }, { key: 'orders', label: '成交订单数' }, { key: 'rate', label: '点击成交率' }],
    rows: [
      { type: '夏季薄款休闲裤', id: '100001', amount: '¥7,280.00', orders: '8', rate: '6.14%' },
      { type: '宽松纯棉短袖', id: '100002', amount: '¥5,460.00', orders: '7', rate: '5.38%' },
      { type: '基础款牛仔外套', id: '100003', amount: '¥3,840.00', orders: '4', rate: '3.72%' }
    ]
  },
  price: {
    title: '价格带构成',
    columns: [{ key: 'type', label: '价格区间' }, { key: 'amount', label: '支付金额' }, { key: 'orders', label: '成交订单数' }, { key: 'avg', label: '客单价' }, { key: 'rate', label: '点击成交率' }],
    rows: [
      { type: '0-50元', amount: '¥3,960.00', orders: '12', avg: '¥330.00', rate: '3.21%' },
      { type: '50-100元', amount: '¥12,680.00', orders: '15', avg: '¥845.33', rate: '4.85%' },
      { type: '100元以上', amount: '¥8,740.00', orders: '8', avg: '¥1,092.50', rate: '4.10%' }
    ]
  },
  audience: {
    title: '人群构成',
    columns: [{ key: 'type', label: '客户类型' }, { key: 'amount', label: '支付金额' }, { key: 'users', label: '成交人数' }, { key: 'avg', label: '客单价' }, { key: 'rate', label: '复购率' }],
    rows: [
      { type: '新客', amount: '¥17,840.00', users: '21', avg: '¥849.52', rate: '8.12%' },
      { type: '老客', amount: '¥7,540.00', users: '8', avg: '¥942.50', rate: '28.60%' }
    ]
  }
}

const REFUND_TABS = [
  { key: 'overview', label: '退款整体数据' },
  { key: 'structure', label: '退款构成' },
  { key: 'reason', label: '退款原因' },
  { key: 'product', label: '退款商品' }
]

const REFUND_SALE_TYPES = [
  { key: 'all', label: '全部' },
  { key: 'self', label: '自营' },
  { key: 'cooperation', label: '合作' }
]

const REFUND_DATA = {
  overview: {
    title: '退款整体数据',
    columns: [
      { key: 'date', label: '日期' },
      { key: 'saleType', label: '售卖类型' },
      { key: 'refundUsers', label: '退款人数' },
      { key: 'refundOrders', label: '退款订单数' },
      { key: 'refundAmount', label: '退款金额', className: 'text-red' },
      { key: 'refundRate', label: '退款率' },
      { key: 'afterSaleUsers', label: '售后人数' },
      { key: 'afterSaleOrders', label: '售后订单数' },
      { key: 'afterSaleAmount', label: '售后金额', className: 'text-red' }
    ],
    rows: [
      { date: '2026/06/21', saleType: '自营', refundUsers: '1', refundOrders: '1', refundAmount: '¥29.74', refundRate: '100.00%', afterSaleUsers: '1', afterSaleOrders: '1', afterSaleAmount: '¥29.74' },
      { date: '2026/06/21', saleType: '合作', refundUsers: '2', refundOrders: '2', refundAmount: '¥86.00', refundRate: '5.41%', afterSaleUsers: '2', afterSaleOrders: '2', afterSaleAmount: '¥86.00' },
      { date: '合计', saleType: '全部', refundUsers: '3', refundOrders: '3', refundAmount: '¥115.74', refundRate: '6.38%', afterSaleUsers: '3', afterSaleOrders: '3', afterSaleAmount: '¥115.74', total: true }
    ]
  },
  structure: {
    title: '退款构成',
    columns: [
      { key: 'stage', label: '退款阶段' },
      { key: 'saleType', label: '售卖类型' },
      { key: 'refundUsers', label: '退款人数' },
      { key: 'refundOrders', label: '退款订单数' },
      { key: 'refundAmount', label: '退款金额', className: 'text-red' },
      { key: 'refundRate', label: '退款率' },
      { key: 'orderRate', label: '订单占比' },
      { key: 'avgAmount', label: '平均退款金额' }
    ],
    rows: [
      { stage: '发货前退款', saleType: '自营', refundUsers: '1', refundOrders: '1', refundAmount: '¥29.74', refundRate: '100.00%', orderRate: '33.33%', avgAmount: '¥29.74' },
      { stage: '未收货退款', saleType: '合作', refundUsers: '1', refundOrders: '1', refundAmount: '¥48.00', refundRate: '3.12%', orderRate: '33.33%', avgAmount: '¥48.00' },
      { stage: '已收货退款', saleType: '合作', refundUsers: '1', refundOrders: '1', refundAmount: '¥38.00', refundRate: '2.29%', orderRate: '33.33%', avgAmount: '¥38.00' },
      { stage: '合计', saleType: '全部', refundUsers: '3', refundOrders: '3', refundAmount: '¥115.74', refundRate: '6.38%', orderRate: '100.00%', avgAmount: '¥38.58', total: true }
    ]
  },
  reason: {
    title: '退款原因',
    columns: [
      { key: 'date', label: '日期' },
      { key: 'caliber', label: '退款口径' },
      { key: 'saleType', label: '售卖类型' },
      { key: 'carrier', label: '载体' },
      { key: 'reasonCategory', label: '原因分类' },
      { key: 'reason', label: '具体原因' },
      { key: 'allUsers', label: '全部退款阶段-退款人数' },
      { key: 'allOrders', label: '全部退款阶段-退款订单数' },
      { key: 'allAmount', label: '全部退款阶段-退款金额', className: 'text-red' },
      { key: 'allRate', label: '全部退款阶段-退款率' },
      { key: 'beforeUsers', label: '发货前退款阶段-退款人数' },
      { key: 'beforeOrders', label: '发货前退款阶段-退款订单数' },
      { key: 'beforeAmount', label: '发货前退款阶段-退款金额', className: 'text-red' },
      { key: 'beforeRate', label: '发货前退款阶段-退款率' },
      { key: 'undeliveredUsers', label: '未收货退款阶段-退款人数' },
      { key: 'undeliveredOrders', label: '未收货退款阶段-退款订单数' },
      { key: 'undeliveredAmount', label: '未收货退款阶段-退款金额', className: 'text-red' },
      { key: 'undeliveredRate', label: '未收货退款阶段-退款率' },
      { key: 'deliveredUsers', label: '已收货退款阶段-退款人数' },
      { key: 'deliveredOrders', label: '已收货退款阶段-退款订单数' },
      { key: 'deliveredAmount', label: '已收货退款阶段-退款金额', className: 'text-red' },
      { key: 'deliveredRate', label: '已收货退款阶段-退款率' }
    ],
    rows: [
      { date: '2026/06/21', caliber: '订单支付时间', saleType: '自营', carrier: '商品卡', reasonCategory: '交付时效', reason: '发货慢', allUsers: '1', allOrders: '1', allAmount: '¥29.74', allRate: '100.00%', beforeUsers: '1', beforeOrders: '1', beforeAmount: '¥29.74', beforeRate: '100.00%', undeliveredUsers: '0', undeliveredOrders: '0', undeliveredAmount: '¥0.00', undeliveredRate: '0.00%', deliveredUsers: '0', deliveredOrders: '0', deliveredAmount: '¥0.00', deliveredRate: '0.00%' },
      { date: '2026/06/21', caliber: '订单支付时间', saleType: '合作', carrier: '直播', reasonCategory: '商品问题', reason: '商品与描述不符', allUsers: '1', allOrders: '1', allAmount: '¥48.00', allRate: '3.12%', beforeUsers: '0', beforeOrders: '0', beforeAmount: '¥0.00', beforeRate: '0.00%', undeliveredUsers: '1', undeliveredOrders: '1', undeliveredAmount: '¥48.00', undeliveredRate: '3.12%', deliveredUsers: '0', deliveredOrders: '0', deliveredAmount: '¥0.00', deliveredRate: '0.00%' },
      { date: '2026/06/21', caliber: '订单支付时间', saleType: '合作', carrier: '短视频', reasonCategory: '客户诉求', reason: '不想要了', allUsers: '1', allOrders: '1', allAmount: '¥38.00', allRate: '2.29%', beforeUsers: '0', beforeOrders: '0', beforeAmount: '¥0.00', beforeRate: '0.00%', undeliveredUsers: '0', undeliveredOrders: '0', undeliveredAmount: '¥0.00', undeliveredRate: '0.00%', deliveredUsers: '1', deliveredOrders: '1', deliveredAmount: '¥38.00', deliveredRate: '2.29%' },
      { date: '合计', caliber: '--', saleType: '全部', carrier: '--', reasonCategory: '--', reason: '--', allUsers: '3', allOrders: '3', allAmount: '¥115.74', allRate: '6.38%', beforeUsers: '1', beforeOrders: '1', beforeAmount: '¥29.74', beforeRate: '100.00%', undeliveredUsers: '1', undeliveredOrders: '1', undeliveredAmount: '¥48.00', undeliveredRate: '3.12%', deliveredUsers: '1', deliveredOrders: '1', deliveredAmount: '¥38.00', deliveredRate: '2.29%', total: true }
    ]
  },
  product: {
    title: '退款商品',
    columns: [
      { key: 'product', label: '商品名称' },
      { key: 'saleType', label: '售卖类型' },
      { key: 'productId', label: '商品ID' },
      { key: 'refundUsers', label: '退款人数' },
      { key: 'refundOrders', label: '退款订单数' },
      { key: 'refundAmount', label: '退款金额', className: 'text-red' },
      { key: 'refundRate', label: '退款率' },
      { key: 'mainReason', label: '主要退款原因' }
    ],
    rows: [
      { product: '夏季薄款休闲裤', saleType: '自营', productId: '100001', refundUsers: '1', refundOrders: '1', refundAmount: '¥29.74', refundRate: '100.00%', mainReason: '发货慢' },
      { product: '宽松纯棉短袖', saleType: '合作', productId: '100002', refundUsers: '1', refundOrders: '1', refundAmount: '¥48.00', refundRate: '3.12%', mainReason: '商品与描述不符' },
      { product: '基础款牛仔外套', saleType: '合作', productId: '100003', refundUsers: '1', refundOrders: '1', refundAmount: '¥38.00', refundRate: '2.29%', mainReason: '不想要了' },
      { product: '合计', saleType: '全部', productId: '--', refundUsers: '3', refundOrders: '3', refundAmount: '¥115.74', refundRate: '6.38%', mainReason: '--', total: true }
    ]
  }
}

export default {
  name: 'NewShopBoardDetailPage',
  components:{UnderDevelopment},
  mounted() {
    this.$nextTick(()=>{
      this.$refs.underDevelopmentRef.show()
      this.initTrendChart()
    })
  },
  beforeDestroy() {
    if (this._trendResizeHandler) {
      window.removeEventListener('resize', this._trendResizeHandler)
      this._trendResizeHandler = null
    }
    if (this._trendChart) {
      this._trendChart.dispose()
      this._trendChart = null
    }
  },
  data() {
    const query = this.$route && this.$route.query ? this.$route.query : {}
    const routeShopId = query.id || query.detail || '1499'
    const routeShopName = query.shop_name || String(routeShopId)
    const shopOptions = [
      { value: String(routeShopId), label: routeShopName },
      { value: '1499', label: '1499' },
      { value: 'star-fashion', label: '星好货企业店' },
      { value: 'trend-shop', label: '潮流服饰店' },
      { value: 'women-shop', label: '星黛我的小店' }
    ].filter((option, index, list) => list.findIndex(item => item.value === option.value || item.label === option.label) === index)
    const secondaryShop = shopOptions.find(option => option.value !== String(routeShopId))
    return {
      activeMode: 'single',
      modeTabs: [{ key: 'multiGroup', label: '多分组' }, { key: 'group', label: '分组' }, { key: 'multiShop', label: '多店' }, { key: 'single', label: '单店' }],
      selectedShopId: String(routeShopId),
      selectedShopIds: [String(routeShopId), secondaryShop ? secondaryShop.value : String(routeShopId)].filter((value, index, list) => list.indexOf(value) === index),
      selectedGroupIds: ['men', 'women'],
      selectedGroup: 'men-women',
      multiGroupOptions: [{ value: 'men', label: '男装组' }, { value: 'women', label: '女装组' }, { value: 'apparel', label: '服装组' }],
      shopOptions,
      groupOptions: [{ value: 'men-women', label: '男装组、女装组' }, { value: 'core-shops', label: '核心店铺组' }, { value: 'new-shops', label: '新店成长组' }],
      dateKey: '30',
      compareDateKey: '30',
      dateOptions: [{ key: 'realtime', label: '实时' }, { key: '1', label: '近1天' }, { key: '7', label: '近7天' }, { key: '30', label: '近30天' }],
      diagnosisExpanded: true,
      multiShopExpanded: true,
      transactionExpanded: true,
      activeTransactionTab: 'terminal',
      transactionTabs: TRANSACTION_TABS,
      overviewSaleType: 'all',
      overviewCarrier: 'all',
      overviewPeriod: 'none',
      overviewSaleTypes: [{ key: 'all', label: '全部' }, { key: 'self', label: '自营' }, { key: 'cooperation', label: '合作' }],
      overviewCarriers: [{ key: 'all', label: '全部' }, { key: 'live', label: '直播' }, { key: 'video', label: '短视频' }, { key: 'card', label: '商品卡' }],
      overviewPeriods: [{ key: 'none', label: '不限' }, { key: 'morning', label: '00:00-12:00' }, { key: 'afternoon', label: '12:00-18:00' }, { key: 'evening', label: '18:00-24:00' }],
      incomeTimeCaliber: 'payment',
      incomeTimeCalibers: [{ key: 'payment', label: '支付时间' }, { key: 'refund', label: '退款时间' }],
      overviewMetrics: [
        { key: 'refundAmount', label: '退款金额(退款时间)', value: '¥154.00', change: '▼ 月环比 -39.94%', tone: 'red' },
        { key: 'payment', label: '用户支付金额', value: '¥745.66', change: '▲ 月环比 +144.96%', tone: 'red' },
        { key: 'orders', label: '成交订单数', value: '9', change: '▲ 月环比 +43.75%', tone: '' },
        { key: 'users', label: '成交人数', value: '6', change: '▲ 月环比 +20.00%', tone: '' },
        { key: 'avg', label: '客单价', value: '¥82.85', change: '▲ 月环比 +103.20%', tone: 'red' },
        { key: 'refundRate', label: '退款率(支付时间)', value: '20.67%', change: '▼ 月环比 -5.12%', tone: 'red' },
        { key: 'exposure', label: '商品曝光人数', value: '3,280', change: '▲ 月环比 +22.10%', tone: 'red' },
        { key: 'clicks', label: '商品点击人数', value: '142', change: '▲ 月环比 +18.40%', tone: 'red' },
        { key: 'conversion', label: '曝光点击转化率', value: '4.33%', change: '▼ 月环比 -2.30%', tone: 'red' }
      ],
      trendLabels: ['6/13', '6/15', '6/17', '6/19', '6/21', '6/23', '6/24'],
      refundExpanded: true,
      activeRefundTab: 'reason',
      refundSaleType: 'all',
      refundTabs: REFUND_TABS,
      refundSaleTypes: REFUND_SALE_TYPES,
      targetAmount: 30000,
      categoryCards: [
        { key: 'hot', label: '爆款', desc: '日目标10件/周订单300', count: 2, amount: '¥12,840', percent: '6.4%', change: '+18%', color: 'var(--gx-red)' },
        { key: 'moving', label: '动销款', desc: '日目标3-10件/周订单200', count: 8, amount: '¥5,210', percent: '26%', change: '+7%', color: '#287ce2' },
        { key: 'flat', label: '平销款', desc: '日目标2-3件/周订单100', count: 5, amount: '¥1,580', percent: '8%', change: '+2%', color: '#54b83e' },
        { key: 'scattered', label: '散单款', desc: '日目标1件/周订单50', count: 12, amount: '¥360', percent: '34%', change: '-1%', color: '#fb8a1b' },
        { key: 'cart', label: '加购款', desc: '点击率≥50/点击率≥4.68%', count: 3, amount: '¥0', percent: '—', change: '—', color: '#7b43d7' },
        { key: 'none', label: '无成交&无加购', desc: '点击量≥30/点击率≤4.68%', count: 5, amount: '¥0', percent: '—', change: '—', color: '#8c8c8c' }
      ],
      categoryColumns: [
        { key: 'label', label: '商品分类' }, { key: 'count', label: '数量' }, { key: 'exposure', label: '曝光人数' }, { key: 'click', label: '点击人数' }, { key: 'clickRate', label: '点击率' }, { key: 'cart', label: '加购人数' }, { key: 'cartRate', label: '加购率' }, { key: 'payRate', label: '支付率' }, { key: 'sales', label: '销量' }, { key: 'avgPrice', label: '客单价' }
      ],
      categoryRows: [
        { label: '爆款', count: 2, exposure: '14317', click: '642', clickRate: '4.68%', cart: '74', cartRate: '11.526%', payRate: '5.645%', sales: '36', avgPrice: '41.46' },
        { label: '动销款', count: 8, exposure: '8920', click: '312', clickRate: '3.50%', cart: '28', cartRate: '8.974%', payRate: '3.205%', sales: '15', avgPrice: '34.73' },
        { label: '平销款', count: 5, exposure: '3450', click: '108', clickRate: '3.13%', cart: '9', cartRate: '8.333%', payRate: '2.778%', sales: '7', avgPrice: '22.57' },
        { label: '散单款', count: 12, exposure: '2840', click: '65', clickRate: '2.29%', cart: '4', cartRate: '6.154%', payRate: '1.538%', sales: '3', avgPrice: '18.00' },
        { label: '加购款', count: 3, exposure: '1820', click: '52', clickRate: '2.86%', cart: '5', cartRate: '9.615%', payRate: '0%', sales: '0', avgPrice: '0' },
        { label: '无成交&无加购', count: 5, exposure: '980', click: '21', clickRate: '2.14%', cart: '0', cartRate: '0%', payRate: '0%', sales: '0', avgPrice: '0' },
        { label: '全店均值', count: 35, exposure: '32327', click: '1200', clickRate: '3.71%', cart: '120', cartRate: '10%', payRate: '3.5%', sales: '61', avgPrice: '32.15', total: true }
      ],
      averageCards: [
        { key: 'hot', label: '爆款均值', color: 'var(--gx-red)', metrics: [{ label: '曝光', value: '7158.5' }, { label: '点击', value: '321' }, { label: '点击率', value: '4.68%' }, { label: '加购', value: '37' }, { label: '加购率', value: '11.526%' }, { label: '支付率', value: '5.645%' }, { label: '销量', value: '18' }, { label: '客单价', value: '41.46' }] },
        { key: 'moving', label: '动销款均值', color: '#287ce2', metrics: [{ label: '曝光', value: '1115' }, { label: '点击', value: '39' }, { label: '点击率', value: '3.50%' }, { label: '加购', value: '3.5' }, { label: '加购率', value: '8.974%' }, { label: '支付率', value: '3.205%' }, { label: '销量', value: '1.875' }, { label: '客单价', value: '34.73' }] },
        { key: 'flat', label: '平销款均值', color: '#54b83e', metrics: [{ label: '曝光', value: '690' }, { label: '点击', value: '21.6' }, { label: '点击率', value: '3.13%' }, { label: '加购', value: '1.8' }, { label: '加购率', value: '8.333%' }, { label: '支付率', value: '2.778%' }, { label: '销量', value: '1.4' }, { label: '客单价', value: '22.57' }] },
        { key: 'scattered', label: '散单款均值', color: '#fb8a1b', metrics: [{ label: '曝光', value: '236.7' }, { label: '点击', value: '5.4' }, { label: '点击率', value: '2.29%' }, { label: '加购', value: '0.33' }, { label: '加购率', value: '6.154%' }, { label: '支付率', value: '1.538%' }, { label: '销量', value: '0.25' }, { label: '客单价', value: '18.00' }] },
        { key: 'cart', label: '加购款均值', color: '#7b43d7', metrics: [{ label: '曝光', value: '606.7' }, { label: '点击', value: '17.3' }, { label: '点击率', value: '2.86%' }, { label: '加购', value: '1.67' }, { label: '加购率', value: '9.615%' }, { label: '支付率', value: '0%' }, { label: '销量', value: '0' }, { label: '客单价', value: '0' }] },
        { key: 'none', label: '无成交&无加购均值', color: '#8c8c8c', metrics: [{ label: '曝光', value: '196' }, { label: '点击', value: '4.2' }, { label: '点击率', value: '2.14%' }, { label: '加购', value: '0' }, { label: '加购率', value: '0%' }, { label: '支付率', value: '0%' }, { label: '销量', value: '0' }, { label: '客单价', value: '0' }] }
      ],
      targetColumns: [{ key: 'label', label: '目标项' }, { key: 'percent', label: '全店成交金额占比' }, { key: 'current', label: '当前数量' }, { key: 'need', label: '完成目标需要' }],
      targetRows: [
        { label: '完成3万需要多少个爆款', percent: '15.49%', current: '2个款', need: '3.1个款' },
        { label: '完成3万需要多少个动销款', percent: '14.19%', current: '8个款', need: '13个款' },
        { label: '完成3万需要多少个平销款', percent: '10.9%', current: '5个款', need: '8个款' },
        { label: '完成3万需要多少个散单款', percent: '59.42%', current: '12个款', need: '19个款' }
      ],
      groupColumns: [
        { key: 'label', label: '分组' }, { key: 'amount', label: '成交额' }, { key: 'trend', label: '趋势' }, { key: 'hot', label: '爆款' }, { key: 'moving', label: '动销款' }, { key: 'flat', label: '平销款' }, { key: 'scattered', label: '散单款' }, { key: 'cart', label: '加购款' }, { key: 'noData', label: '无成交&无加购' }, { key: 'action', label: '近期动作' }, { key: 'rating', label: '综合评级' }
      ],
      groupRows: [
        { label: '男装组', amount: '¥25,380', trend: '-2%', hot: '4款', moving: '13款', flat: '8款', scattered: '17款', cart: '0款', noData: '19款', action: '部分店铺有优化', rating: '良' },
        { label: '女装组', amount: '¥11,420', trend: '+12%', hot: '2款', moving: '6款', flat: '4款', scattered: '9款', cart: '0款', noData: '7款', action: '大促参与+短视频', rating: '良' },
        { label: '均值', amount: '¥18,400', trend: '--', hot: '', moving: '', flat: '', scattered: '', cart: '', noData: '', action: '', rating: '', total: true }
      ],
      groupSummaryCards: [
        { key: 'amount', label: '全组总成交额', value: '¥39,710', note: '环比+12%' },
        { key: 'products', label: '全组总商品数', value: '120款', note: '' },
        { key: 'hot', label: '全组爆款数', value: '6款', note: '占比5%' },
        { key: 'moving', label: '全组动销款数', value: '22款', note: '占比18%' },
        { key: 'flat', label: '全组平销款+散单款', value: '35款', note: '占比29%' },
        { key: 'none', label: '全组无成交&无加购款', value: '57款', note: '占比48%' }
      ],
      groupShopColumns: [
        { key: 'shop', label: '店铺' }, { key: 'amount', label: '成交额' }, { key: 'trend', label: '趋势' }, { key: 'hot', label: '爆款' }, { key: 'moving', label: '动销款' }, { key: 'flat', label: '平销款' }, { key: 'scattered', label: '散单款' }, { key: 'cart', label: '加购款' }, { key: 'noData', label: '无成交&无加购' }, { key: 'action', label: '近期动作' }, { key: 'rating', label: '综合评级' }
      ],
      groupShopRows: [
        { shop: '1499', amount: '¥19,100', trend: '+24%', hot: '3款', moving: '8款', flat: '5款', scattered: '12款', cart: '3款', noData: '5款', action: '商品卡广告+主图优化', rating: '优' },
        { shop: '星好货企业店', amount: '¥6,280', trend: '-8%', hot: '1款', moving: '5款', flat: '3款', scattered: '14款', cart: '1款', noData: '12款', action: '无近期动作', rating: '差' },
        { shop: '分组均值', amount: '¥12,690', trend: '--', hot: '2款', moving: '7款', flat: '4款', scattered: '13款', cart: '2款', noData: '9款', action: '—', rating: '良', total: true }
      ],
      groupDiagnosisConclusion: '分组诊断：4家店铺中2家评级为差（星好货企业店、星黛潮流旗舰店），全组48%商品为无成交&无加购，拉低整体效率。爆款仅6款，需至少12款才能达到分组目标¥60,000。',
      groupDiagnosisActions: [
        '爆款缺口：全组爆款仅6款，需新增12款。建议从动销款中筛选3款加大推广力度升级为爆款',
        '差店改善：星好货企业店和星黛潮流旗舰店无近期动作，建议立即执行主图优化+商品卡广告投放',
        '无成交&无加购清理：全组57款无成交&无加购，建议批量清理或重新优化标题关键词，释放店铺权重'
      ],
      multiShopColumns: [
        { key: 'shop', label: '店铺' }, { key: 'group', label: '分组' }, { key: 'amount', label: '成交额' }, { key: 'trend', label: '趋势' }, { key: 'hot', label: '爆款' }, { key: 'moving', label: '动销款' }, { key: 'flat', label: '平销款' }, { key: 'scattered', label: '散单款' }, { key: 'cart', label: '加购款' }, { key: 'noData', label: '无成交&无加购' }, { key: 'action', label: '近期动作' }, { key: 'rating', label: '综合评级' }
      ],
      multiShopRows: [
        { groupHeader: '男装组 — 2家店铺 | 合计 ¥25,380' },
        { shop: '1499', group: '男装组', amount: '¥19,100', trend: '+24%', hot: '3款', moving: '8款', flat: '5款', scattered: '12款', cart: '3款', noData: '5款', action: '商品卡广告+主图优化', rating: '优' },
        { shop: '星好货企业店', group: '男装组', amount: '¥6,280', trend: '-8%', hot: '1款', moving: '5款', flat: '3款', scattered: '14款', cart: '1款', noData: '12款', action: '无近期动作', rating: '差' },
        { groupHeader: '女装组 — 1家店铺 | 合计 ¥11,420' },
        { shop: '星黛我的小店', group: '女装组', amount: '¥11,420', trend: '+12%', hot: '2款', moving: '6款', flat: '4款', scattered: '8款', cart: '2款', noData: '7款', action: '大促参与+短视频', rating: '良' },
        { shop: '总均值', group: '', amount: '¥12,267', trend: '--', hot: '', moving: '', flat: '', scattered: '', cart: '', noData: '', action: '', rating: '', total: true }
      ],
      shopSuggestionData: [
        { shop: '1499', summary: '商品卡广告与主图优化已带动成交额增长24%，可继续放大爆款投放。', items: ['爆款库存维持7天以上安全量', '复制高点击主图到5款动销商品', '保持商品卡广告的分时投放'], result: '预期效果：月成交额稳定在¥22,000+' },
        { shop: '星好货企业店', summary: '14款无成交&无加购，成交额¥6,280（-8%），无爆款突破。', items: ['参考同组1499店铺爆款主图和价格对标', '投放商品卡广告或短视频带动曝光', '清理12款无成交无加购商品'], result: '预期效果：执行后预计月成交额提升至¥9,000+' },
        { shop: '潮流服饰店', summary: '散单款数量偏高，近期缺少有效优化动作。', items: ['筛选3款潜力商品重点投放', '优化商品标题和价格带', '降低低效商品曝光权重'], result: '预期效果：预计减少低效商品30%' },
        { shop: '星黛我的小店', summary: '大促与短视频带动增长，但爆款数量仍可提升。', items: ['延续短视频种草节奏', '为动销款增加直播切片素材', '补充2款高客单爆款'], result: '预期效果：预计月成交额提升15%' }
      ],
      optimizationActions: [
        { date: '07-12', title: '优化主图+价格调整', note: '节收意愿提升预期', beforeFirst: '点击率 1.8%', beforeSecond: '成交率 0.5%', afterFirst: '点击率 2.3%', afterSecond: '成交率 0.6%', changeFirst: '点击率 +28%', changeSecond: '成交率 +20%' },
        { date: '07-08', title: '投放商品卡广告', note: '商务合作佣（深灰）', beforeFirst: '曝光450', beforeSecond: '成交2单', afterFirst: '曝光1980', afterSecond: '成交7单', changeFirst: '曝光+340%', changeSecond: '成交+5单' },
        { date: '07-03', title: '参与平台大促活动', note: '全店商品', beforeFirst: '日成交额¥420', beforeSecond: '—', afterFirst: '日成交额¥680', afterSecond: '—', changeFirst: '成交额+¥260/天', changeSecond: '退款率持平' }
      ],
      suggestions: [{ shop: '星好货企业店', summary: '14款无成交&无加购，成交额¥6,280（-8%），无爆款突破', items: ['参考同组1499店铺爆款主图/价格对标', '投放商品卡广告或短视频带动，目标提升曝光≥20%', '清理12款无成交无加购款，释放库存并重新梳理商品'], result: '预期效果：执行后预计月成交额提升至¥9,000+' }],
      diagnosisConclusion: '爆款仅2款（占比5.7%），动销款占比偏低，散单款占比过高，当前店铺成交结构仍有较大优化空间。建议优先提升爆款和动销款数量，减少长期无成交商品。',
      revenueSuggestions: [
        { label: '爆款建议', text: '快速迭代主图和库存预警，建议补货至200件，预计带来¥8,000+成交额', icon: '↑', tone: 'red' },
        { label: '动销款建议', text: '8款动销款中3款点击成交率低于0.05%，建议优化主图或降低投放门槛，预计提升成交率0.3%', icon: '→', tone: 'blue' },
        { label: '平销款建议', text: '5款平销款加强短视频曝光，预计月增¥3,270成交额', icon: '→', tone: 'green' },
        { label: '散单款建议', text: '12款散单款中筛选3款潜力款重点推广，其余9款考虑下架或降权', icon: '!', tone: 'orange' },
        { label: '无成交&无加购建议', text: '5款商品已超过21天无流量，建议下架或调整标题关键词', icon: '!', tone: 'gray' }
      ]
    }
  },
  computed: {
    pageTitle() {
      return String(this.$route && this.$route.query && this.$route.query.platform) === '7' ? '微信小店数据分析' : '抖店数据分析'
    },
    diagnosisTitle() {
      return '店铺诊断'
    },
    selectorLabel() {
      if (this.activeMode === 'single') return '店铺'
      if (this.activeMode === 'multiShop') return '店铺范围'
      return '分组'
    },
    selectorOptions() {
      if (this.activeMode === 'single') return this.shopOptions
      return this.groupOptions
    },
    groupDiagnosisScope() {
      const selected = this.groupOptions.find(option => option.value === this.selectedGroup)
      return selected ? selected.label : '分组'
    },
    selectedGroupList() {
      const ids = this.selectedGroupIds && this.selectedGroupIds.length ? this.selectedGroupIds : this.multiGroupOptions.slice(0, 2).map(option => option.value)
      return this.multiGroupOptions.filter(option => ids.indexOf(option.value) > -1)
    },
    displayGroupRows() {
      if (this.activeMode !== 'multiGroup') return this.groupRows
      const labels = this.selectedGroupList.map(option => option.label)
      const rows = this.groupRows.filter(row => row.total || labels.indexOf(row.label) > -1)
      return rows.length ? rows : this.groupRows
    },
    filteredMultiGroupRows() {
      const labels = this.selectedGroupList.map(option => option.label)
      const rows = this.multiShopRows.filter(row => {
        if (row.total) return true
        if (row.groupHeader) return labels.some(label => row.groupHeader.indexOf(label) > -1)
        return labels.indexOf(row.group) > -1
      })
      return rows.length ? rows : this.multiShopRows
    },
    selectorValue: {
      get() {
        if (this.activeMode === 'single') return this.selectedShopId
        return this.selectedGroup
      },
      set(value) {
        if (this.activeMode === 'single') this.selectedShopId = value
        else this.selectedGroup = value
      }
    },
    dateRangeText() {
      const ranges = { realtime: '2026-07-15', '1': '2026-07-14 - 2026-07-15', '7': '2026-07-09 - 2026-07-15', '30': '2026-06-16 - 2026-07-15' }
      return ranges[this.dateKey] || ranges['30']
    },
    currentTransaction() {
      return TRANSACTION_DATA[this.activeTransactionTab] || TRANSACTION_DATA.terminal
    },
    compareDateRangeText() {
      const ranges = { realtime: '2026-07-15', '1': '2026-07-14 - 2026-07-15', '7': '2026-07-09 - 2026-07-15', '30': '2026-06-16 - 2026-07-15' }
      return ranges[this.compareDateKey] || ranges['30']
    },
    selectedShopList() {
      const selectedIds = this.selectedShopIds && this.selectedShopIds.length ? this.selectedShopIds : this.shopOptions.slice(0, 2).map(option => option.value)
      return this.shopOptions.filter(option => selectedIds.indexOf(option.value) > -1)
    },
    comparisonDataRows() {
      return this.multiShopRows.filter(row => !row.groupHeader && !row.total)
    },
    selectedComparisonRows() {
      const selectedNames = this.selectedShopList.map(option => option.label)
      const rows = this.comparisonDataRows.filter(row => selectedNames.indexOf(row.shop) > -1)
      return rows.length ? rows : this.comparisonDataRows.slice(0, 2)
    },
    multiShopAverageRow() {
      const rows = this.selectedComparisonRows
      if (!rows.length) return null
      const average = key => `${Math.round(rows.reduce((sum, row) => sum + Number(String(row[key]).replace(/[^\d.-]/g, '')), 0) / rows.length)}款`
      const averageAmount = rows.reduce((sum, row) => sum + Number(String(row.amount).replace(/[^\d.-]/g, '')), 0) / rows.length
      return {
        shop: '均值', group: '', amount: `¥${Math.round(averageAmount).toLocaleString()}`, trend: '--', hot: average('hot'), moving: average('moving'), flat: average('flat'), scattered: average('scattered'), cart: average('cart'), noData: average('noData'), action: '', rating: '', total: true
      }
    },
    filteredMultiShopDiagnosisRows() {
      return this.multiShopAverageRow ? this.selectedComparisonRows.concat(this.multiShopAverageRow) : this.selectedComparisonRows
    },
    filteredMultiShopCompareRows() {
      return this.filteredMultiShopDiagnosisRows
    },
    singleModeCompareRows() {
      const rows = this.comparisonDataRows
      if (!rows.length) return rows
      const average = key => `${Math.round(rows.reduce((sum, row) => sum + Number(String(row[key]).replace(/[^\d.-]/g, '')), 0) / rows.length)}款`
      const averageAmount = rows.reduce((sum, row) => sum + Number(String(row.amount).replace(/[^\d.-]/g, '')), 0) / rows.length
      return rows.concat({ shop: '均值', group: '', amount: `¥${Math.round(averageAmount).toLocaleString()}`, trend: '--', hot: average('hot'), moving: average('moving'), flat: average('flat'), scattered: average('scattered'), cart: average('cart'), noData: average('noData'), action: '', rating: '', total: true })
    },
    multiShopCompareRows() {
      return this.activeMode === 'single' ? this.singleModeCompareRows : this.filteredMultiShopCompareRows
    },
    compareSuggestions() {
      const shops = this.activeMode === 'single' ? this.comparisonDataRows.map(row => row.shop) : this.selectedComparisonRows.map(row => row.shop)
      const matching = this.shopSuggestionData.filter(item => shops.indexOf(item.shop) > -1)
      return matching.length ? matching : this.shopSuggestionData.slice(0, 1)
    },
    currentRefund() {
      return REFUND_DATA[this.activeRefundTab] || REFUND_DATA.reason
    },
    filteredRefundRows() {
      if (this.refundSaleType === 'all') return this.currentRefund.rows
      const selectedType = REFUND_SALE_TYPES.find(type => type.key === this.refundSaleType)
      if (!selectedType) return this.currentRefund.rows
      return this.currentRefund.rows.filter(row => row.total || row.saleType === selectedType.label)
    }
  },
  watch: {
    activeTransactionTab(value) {
      if (value === 'overview') this.$nextTick(this.initTrendChart)
    },
    transactionExpanded(expanded) {
      if (expanded && this.activeTransactionTab === 'overview') this.$nextTick(this.initTrendChart)
    }
  },
  methods: {
    goBack() {
      if (!this.$router) return
      if (this.$route && this.$route.path === '/new_ops_workbench' && this.$route.query && this.$route.query.detail) {
        this.$router.replace({ path: '/new_ops_workbench', query: { menu: 'ops-shop' } }).catch(() => {})
        return
      }
      this.$router.go(-1)
    },
    selectMode(mode) {
      this.activeMode = mode
      if (mode === 'multiShop' && !this.selectedShopIds.length) {
        this.selectedShopIds = this.shopOptions.slice(0, 2).map(option => option.value)
      }
      if (mode === 'multiGroup' && !this.selectedGroupIds.length) {
        this.selectedGroupIds = this.multiGroupOptions.slice(0, 2).map(option => option.value)
      }
    },
    selectDate(key) {
      this.dateKey = key
    },
    selectCompareDate(key) {
      this.compareDateKey = key
    },
    changeClass(value) {
      if (!value || value === '--' || value === '—') return 'text-gray'
      return String(value).indexOf('-') === 0 ? 'text-red' : 'text-green'
    },
    ratingClass(value) {
      if (value === '优') return 'is-good'
      if (value === '差') return 'is-bad'
      return 'is-normal'
    },
    initTrendChart() {
      const chartElement = this.$refs.trendChart
      if (!chartElement || this.activeTransactionTab !== 'overview' || !this.transactionExpanded) return
      if (!this._trendChart) {
        this._trendChart = echarts.init(chartElement)
        this._trendResizeHandler = () => {
          if (this._trendChart) this._trendChart.resize()
        }
        window.addEventListener('resize', this._trendResizeHandler)
      }
      this._trendChart.setOption({
        animation: false,
        color: ['#d95b52', '#287ce2', '#54b83e', '#9aa1ad', '#c1c7d0'],
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'line' }
        },
        legend: {
          top: 0,
          right: 0,
          icon: 'circle',
          itemWidth: 8,
          itemHeight: 8,
          itemGap: 14,
          textStyle: { color: '#8f98a7', fontSize: 10 }
        },
        grid: { top: 30, right: 18, bottom: 28, left: 12, containLabel: true },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.trendLabels,
          axisLine: { lineStyle: { color: '#dfe3eb' } },
          axisTick: { show: false },
          axisLabel: { color: '#98a0ad', fontSize: 10 }
        },
        yAxis: [
          {
            type: 'value',
            splitNumber: 4,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { color: '#98a0ad', fontSize: 10 },
            splitLine: { lineStyle: { color: '#edf0f4', type: 'dashed' } }
          },
          {
            type: 'value',
            splitNumber: 4,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false }
          }
        ],
        series: [
          {
            name: '用户支付金额',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            data: [420, 580, 510, 760, 680, 920, 860],
            lineStyle: { width: 2 },
            areaStyle: { color: 'rgba(217, 91, 82, 0.08)' }
          },
          {
            name: '成交订单数',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            symbol: 'circle',
            symbolSize: 4,
            data: [180, 240, 210, 300, 270, 265, 305],
            lineStyle: { width: 2 }
          },
          {
            name: '成交人数',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            symbol: 'circle',
            symbolSize: 4,
            data: [15, 19, 18, 24, 22, 30, 29],
            lineStyle: { width: 2 }
          },
          {
            name: '退款金额',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            symbol: 'none',
            data: [32, 28, 45, 36, 42, 38, 46],
            lineStyle: { width: 1.5, type: 'dashed' }
          },
          {
            name: '商品曝光人数',
            type: 'line',
            smooth: true,
            symbol: 'none',
            data: [260, 320, 300, 420, 390, 510, 560],
            lineStyle: { width: 1.5, type: 'dashed' }
          }
        ]
      }, true)
      this._trendChart.resize()
    }
  }
}
</script>

<style scoped>
.gx-sb-detail { min-width: 0; min-height: 100vh; padding-bottom: 24px; background: #f5f6f9; color: #2c3444; font-family: "Noto Sans SC", sans-serif; font-size: 12px; }
.gx-sb-detail button, .gx-sb-detail select, .gx-sb-detail input { font-family: inherit; }
.gx-sb-detail__header { display: flex; align-items: center; height: 48px; padding: 0 18px; background: #fff; border-bottom: 1px solid #e3e6ed;gap: 12px; }
.gx-sb-detail__header h1 { margin: 0 0 0 12px; color: var(--gx-black); font-size: 16px; font-weight: 700; }
.gx-sb-detail__header .divider-line {width: 1px;  height: 18px;  background: rgb(224, 224, 236); }
.gx-sb-detail__back { display: inline-flex; align-items: center; gap: 4px; padding: 0; border: 0; background: transparent; color: var(--gx-black6); cursor: pointer; font-size: 13px; }
.gx-sb-detail__back:hover { color: var(--gx-red); }
.gx-sb-detail__back-icon { font-size: 20px; line-height: 12px; }
.gx-sb-detail__modebar { display: flex; align-items: center; min-height: 42px; padding: 0 18px; background: #fff; border-bottom: 1px solid #e2e5ec; }
.gx-sb-detail__modes { display: flex; align-self: stretch; gap: 2px; }
.gx-sb-detail__mode { min-width: 58px; padding: 0 18px; border: 0; border-bottom: 2px solid transparent; background: transparent; color: var(--gx-black6); cursor: pointer; font-size: 13px; }
.gx-sb-detail__mode:hover, .gx-sb-detail__mode--active { color: var(--gx-red); }
.gx-sb-detail__mode--active { border-bottom-color: var(--gx-red); font-weight: 600; }
.gx-sb-detail__scope { display: inline-flex; align-items: center; gap: 8px; margin-left: 18px; padding-left: 18px; border-left: 1px solid #e5e7ed; color: #687287; white-space: nowrap; }
.gx-sb-detail__scope select { width: 168px; height: 26px; padding: 0 8px; border: 1px solid #d8dde7; border-radius: 3px; outline: none; background: #fff; color: #39445b; font-size: 12px; }
.gx-sb-detail__shop-multi-select { width: 230px; }
.gx-sb-detail__group-multi-select { width: 168px; }
.gx-sb-detail__content { padding: 16px; }
.gx-sb-detail__panel { margin-bottom: 14px; overflow: hidden; border: 1px solid #dce1eb; border-radius: 6px; background: #fff; box-shadow: 0 1px 3px rgba(42, 55, 84, .06); }
.gx-sb-detail__panel-head { display: flex; align-items: center; justify-content: space-between; min-height: 38px; padding: 0 14px; border-bottom: 1px solid #e5e8ee; }
.gx-sb-detail__panel-title { display: inline-flex; align-items: center; gap: 7px; color: #2c3444; font-size: 13px; font-weight: 700; }
.gx-sb-detail__title-bar { width: 3px; height: 14px; border-radius: 2px; background: var(--gx-red); }
.gx-sb-detail__title-bar--blue { background: #3d8ee8; }
.gx-sb-detail__title-bar--orange { background: #f08a24; }
.gx-sb-detail__panel-actions { display: flex; align-items: center; gap: 7px; }
.gx-sb-detail__date { height: 24px; padding: 0 9px; border: 1px solid #e0e3e9; border-radius: 3px; background: #fff; color: #687287; cursor: pointer; font-size: 11px; }
.gx-sb-detail__date:hover, .gx-sb-detail__date--active { border-color: #ef7c78; color: var(--gx-red); }
.gx-sb-detail__date-range { margin-left: 3px; color: #9aa2b1; font-size: 11px; white-space: nowrap; }
.gx-sb-detail__collapse { width: 22px; height: 24px; margin-left: 8px; padding: 0; border: 0; background: transparent; color: #929aa9; cursor: pointer; font-size: 16px; }
.gx-sb-detail__panel-body{ padding: 12px 14px 14px; }
.gx-sb-detail__overview-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; min-height: 38px; color: #727d8f; font-size: 11px; }
.gx-sb-detail__overview-toolbar button { height: 24px; padding: 0 10px; border: 1px solid #e2e5eb; border-radius: 4px; background: #fff; color: #7a8495; cursor: pointer; font-size: 11px; }
.gx-sb-detail__overview-toolbar button:hover, .gx-sb-detail__overview-toolbar button.is-active { border-color: var(--gx-red); color: var(--gx-red); }
.gx-sb-detail__overview-toolbar i { width: 1px; height: 18px; margin: 0 4px; background: #e4e7ed; }
.gx-sb-detail__overview-toolbar select { min-width: 92px; height: 24px; padding: 0 8px; border: 1px solid #d8dde7; border-radius: 3px; background: #fff; color: #39445b; font-size: 11px; }
.gx-sb-detail__income-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; min-height: 38px; color: #727d8f; font-size: 11px; }
.gx-sb-detail__income-toolbar button { height: 24px; padding: 0 10px; border: 1px solid #e2e5eb; border-radius: 4px; background: #fff; color: #7a8495; cursor: pointer; font-size: 11px; }
.gx-sb-detail__income-toolbar button:hover, .gx-sb-detail__income-toolbar button.is-active { border-color: var(--gx-red); color: var(--gx-red); }
.gx-sb-detail__income-toolbar select { min-width: 92px; height: 24px; padding: 0 8px; border: 1px solid #d8dde7; border-radius: 3px; background: #fff; color: #39445b; font-size: 11px; }
.gx-sb-detail__income-toolbar i { width: 1px; height: 18px; margin: 0 4px; background: #e4e7ed; }
.gx-sb-detail__income-caliber-label { margin-left: 2px; }
.gx-sb-detail__subhead--income { margin-top: 5px; display: flex; align-items: center; gap: 8px; color: #2c3444; font-weight: 700; }
.gx-sb-detail__income-sections { display: flex; flex-direction: column; gap: 10px; }
.gx-sb-detail__income-section { overflow: hidden; border: 1px solid #dce1eb; border-radius: 6px; background: #fff; }
.gx-sb-detail__income-section-head { display: flex; align-items: center; min-height: 28px; padding: 0 10px; border-bottom: 1px solid #e8ebf1; font-size: 11px; }
.gx-sb-detail__income-section-head .gx-sb-detail__title-bar { margin-right: 6px; }
.gx-sb-detail__income-section.is-red .gx-sb-detail__income-section-head { background: #fff4f3; color: #d95b52; }
.gx-sb-detail__income-section.is-orange .gx-sb-detail__income-section-head { background: #fff8ef; color: #ef8a25; }
.gx-sb-detail__income-section.is-blue .gx-sb-detail__income-section-head { background: #f1f6ff; color: #3d82dc; }
.gx-sb-detail__income-section.is-green .gx-sb-detail__income-section-head { background: #f1faee; color: #55ae45; }
.gx-sb-detail__income-formula { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; min-height: 52px; padding: 9px 12px; color: #667286; font-size: 11px; }
.gx-sb-detail__income-formula > strong { color: #465166; font-size: 11px; white-space: nowrap; }
.gx-sb-detail__income-formula > strong em { margin-left: 4px; color: #8f98a7; font-style: normal; font-weight: 500; }
.gx-sb-detail__income-formula > b, .gx-sb-detail__income-plus { color: #98a0ad; font-weight: 500; }
.gx-sb-detail__income-chip { display: inline-flex; align-items: center; min-height: 28px; padding: 0 10px; border: 1px solid #e1e5ed; border-radius: 4px; background: #fbfbfd; color: #8490a2; white-space: nowrap; }
.gx-sb-detail__income-section.is-red .gx-sb-detail__income-chip { border-color: #f4d5d2; background: #fff7f6; }
.gx-sb-detail__income-section.is-orange .gx-sb-detail__income-chip { border-color: #f7dfc5; background: #fffaf4; }
.gx-sb-detail__income-section.is-blue .gx-sb-detail__income-chip { border-color: #d4e3fb; background: #f5f8ff; }
.gx-sb-detail__income-section.is-green .gx-sb-detail__income-chip { border-color: #d9efd2; background: #f6fcf3; }
.gx-sb-detail__overview-metrics { display: grid; grid-template-columns: repeat(5, minmax(150px, 1fr)); gap: 9px; }
.gx-sb-detail__overview-metric { min-height: 70px; padding: 9px 10px 7px; border: 1px solid #dfe3eb; border-radius: 5px; background: #fff; box-shadow: 0 1px 2px rgba(42, 55, 84, .05); }
.gx-sb-detail__overview-metric span, .gx-sb-detail__overview-metric small { display: block; color: #8b94a4; font-size: 10px; }
.gx-sb-detail__overview-metric strong { display: block; margin: 7px 0 3px; color: #2c3444; font-family: "JetBrains Mono", monospace; font-size: 16px; }
.gx-sb-detail__overview-metric strong.is-red { color: #e0615b; }
.gx-sb-detail__overview-metric small { color: #55ae45; }
.gx-sb-detail__overview-metric small.is-negative { color: #e0615b; }
.gx-sb-detail__trend-box { margin-top: 12px; padding: 12px 10px 7px; border: 1px solid #dfe3eb; border-radius: 5px; background: #fff; }
.gx-sb-detail__trend-head { display: flex; align-items: center; justify-content: space-between; color: #3e485c; font-size: 11px; }
.gx-sb-detail__trend-legend { display: flex; flex-wrap: wrap; gap: 8px; color: #9aa1ad; font-size: 10px; }
.gx-sb-detail__trend-legend .is-red { color: #d95b52; }
.gx-sb-detail__trend-legend .is-blue { color: #287ce2; }
.gx-sb-detail__trend-legend .is-green { color: #54b83e; }
.gx-sb-detail__trend-chart { position: relative; width: 100%; height: 230px; margin-top: 8px; }
.gx-sb-detail__trend-labels { display: flex; justify-content: space-between; padding: 4px 2px 0; color: #98a0ad; font-size: 10px; }
.gx-sb-detail__subhead { margin: 1px 0 8px; color: var(--gx-black6); font-size: 12px; }
.gx-sb-detail__subhead--table, .gx-sb-detail__subhead--target, .gx-sb-detail__subhead--action, .gx-sb-detail__subhead--transaction { margin-top: 12px; }
.gx-sb-detail__status-grid, .gx-sb-detail__average-grid { display: grid; grid-template-columns: repeat(6, minmax(150px, 1fr)); gap: 9px; overflow-x: auto; }
.gx-sb-detail__group-summary-grid { display: grid; grid-template-columns: repeat(6, minmax(150px, 1fr)); gap: 9px; overflow-x: auto; margin-bottom: 12px; }
.gx-sb-detail__group-summary-card { min-height: 68px; padding: 9px 10px; border: 1px solid #dfe3eb; border-radius: 4px; background: #fff; }
.gx-sb-detail__group-summary-card span, .gx-sb-detail__group-summary-card small { display: block; color: #8b94a4; font-size: 10px; }
.gx-sb-detail__group-summary-card strong { display: block; margin: 7px 0 3px; color: #313b50; font-size: 14px; font-weight: 700; }
.gx-sb-detail__group-summary-card small { color: #55ae45; }
.gx-sb-detail__group-conclusion { margin-top: 12px; padding: 9px 10px; border: 1px solid #f2c9c6; border-radius: 4px; background: #fff5f4; color: var(--gx-red); font-size: 11px; line-height: 1.6; font-weight: 600; }
.gx-sb-detail__group-actions { display: flex; flex-direction: column; gap: 6px; margin-top: 10px; }
.gx-sb-detail__group-action {    display: flex;
  gap: 8px;
  padding: 8px 10px;
  background: rgb(245, 245, 247);
  border-radius: 6px;
  border: 1px solid rgb(224, 224, 236);
  font-family: "Noto Sans SC", sans-serif;
  font-size: 12px;
  color: var(--gx-black);
}
.gx-sb-detail__group-action > span { display: inline-flex; align-items: center; justify-content: center; width: 15px; height: 15px; border: 1px solid  var(--gx-red); border-radius: 50%; color: var(--gx-red); font-size: 9px; }
.gx-sb-detail__status-card { min-height: 104px; padding: 10px 11px 8px; border: 2px solid #dfe3eb; border-top: 3px solid; border-radius: 6px; background: #fff; }
.gx-sb-detail__status-name { font-size: 12px; font-weight: 700; }
.gx-sb-detail__status-desc { min-height: 25px; margin-top: 4px; color: #a0a7b3; font-size: 10px; line-height: 1.35; }
.gx-sb-detail__status-value { margin-top: 5px; color: #2c3444; font-size: 14px; font-weight: 700; }
.gx-sb-detail__status-amount { margin-top: 1px; color: #2c3444; font-family: "JetBrains Mono", monospace; font-size: 12px; }
.gx-sb-detail__status-foot { display: flex; justify-content: space-between; margin-top: 4px; color: #8d96a5; font-size: 10px; }
.gx-sb-detail__table-wrap { width: 100%; overflow-x: auto; }
.gx-sb-detail__table-wrap--border{
  background: rgb(255, 255, 255);
  border-radius: 8px;
  border: 2px solid rgb(224, 224, 236);
}
.gx-sb-detail__table { width: 100%; min-width: 930px; border-collapse: collapse; color: #3e485c; white-space: nowrap; }
.gx-sb-detail__table th, .gx-sb-detail__table td { height: auto;padding: 7px 10px;; border-bottom: 1px solid #edf0f4; text-align: left;  }
.gx-sb-detail__table th { background: #f4f0e9; color:var(--gx-black6); font-weight: 500;font-size: 11px; }
.gx-sb-detail__table td {color: var(--gx-black);font-size: 12px;}
.gx-sb-detail__table tbody tr td{font-weight: 400;}
.gx-sb-detail__table tbody tr:nth-child(even) { background: rgb(245, 245, 247);}
.gx-sb-detail__table tbody tr:hover { background: #fff8f6; }
.gx-sb-detail__table tbody tr.is-total td{ background: #f5f0e8; font-weight: 600; }
.gx-sb-detail__table tbody tr.is-group { background:rgb(240, 240, 245); }
.gx-sb-detail__table tbody tr.is-group td {
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 600;
  color: var(--gx-black6);
  text-align: left; }
.gx-sb-detail__table--category th:first-child, .gx-sb-detail__table--category td:first-child, .gx-sb-detail__table--target th:first-child, .gx-sb-detail__table--target td:first-child { text-align: left; }
.gx-sb-detail__average-card { padding: 9px 10px 8px; border: 1px solid #dfe3eb; border-top: 3px solid; border-radius: 4px; background: #fff; }
.gx-sb-detail__average-title { margin-bottom: 7px; font-size: 11px; font-weight: 700; }
.gx-sb-detail__average-row { display: flex; justify-content: space-between; height: 17px; color: #929aa9; font-size: 10px; }
.gx-sb-detail__average-row strong { color: #354056; font-family: "JetBrains Mono", monospace; font-weight: 500; }
.gx-sb-detail__target-input { display: inline-flex; align-items: center; gap: 9px; margin-bottom: 7px; color: #7d8798; font-size: 11px; }
.gx-sb-detail__target-input input { width: 82px; height: 24px; padding: 0 7px; border: 1px solid #d9dee8; border-radius: 3px; outline: none; color: #44506a; font-family: "JetBrains Mono", monospace; font-size: 11px; }
.gx-sb-detail__target-input input:focus { border-color: #ef7c78; }
.gx-sb-detail__hint { margin: 4px 0 0; color: #a1a8b4; font-size: 10px; }
.gx-sb-detail__action-card { padding: 10px; border: 1px solid #dce1eb; border-radius: 5px; background: #fbfbfd; }
.gx-sb-detail__action-meta { display: flex; gap: 10px; align-items: center; margin-bottom: 8px; color: #8791a2; font-size: 10px; }
.gx-sb-detail__action-card~.gx-sb-detail__action-card{
  margin-top: 8px;
}
.gx-sb-detail__action-meta strong { color: #3f4a60; }
.gx-sb-detail__action-columns { display: grid; grid-template-columns: minmax(180px, 1fr) 24px minmax(180px, 1fr) minmax(180px, 1fr); gap: 8px; align-items: center; }
.gx-sb-detail__action-columns > div { display: flex; flex-direction: column; gap: 3px; min-height: 54px; padding: 7px 9px; border: 1px solid #e2e5eb; border-radius: 3px; background: #fff; color: #7c8697; font-size: 10px; }
.gx-sb-detail__action-arrow { color: #9ba4b2; font-size: 18px; line-height: 1; text-align: center; }
.gx-sb-detail__action-columns > div.is-positive { padding-left: 0; border: 0; background: transparent; }
.gx-sb-detail__action-columns strong { color: #4b566b; font-weight: 500;font-size: 11px; }
.gx-sb-detail__action-columns .is-positive strong { color: var(--gx-green);font-weight: 600; }
.gx-sb-detail__subhead--conclusion, .gx-sb-detail__subhead--revenue { margin-top: 12px; }
.gx-sb-detail__conclusion { padding: 9px 10px; border: 1px solid #f2c9c6; border-radius: 4px; background: #fff5f4; color: var(--gx-red); font-size: 11px; line-height: 1.6;font-weight: 700; }
.gx-sb-detail__revenue-list { display: flex; flex-direction: column; gap: 6px; }
.gx-sb-detail__revenue-item { display: flex; align-items: center; min-height: 30px; padding: 0 10px; border: 1px solid #e1e5ed; border-radius: 4px; background: #f8f9fb; color: var(--gx-black); font-size: 12px; }
.gx-sb-detail__revenue-item strong { color: #445069; font-weight: 600; }
.gx-sb-detail__revenue-icon { display: inline-flex; align-items: center; justify-content: center; width: 18px; margin-right: 7px; font-size: 14px; font-weight: 700; }
.gx-sb-detail__revenue-icon.is-red { color: #e45c55; }
.gx-sb-detail__revenue-icon.is-blue { color: #3d82dc; }
.gx-sb-detail__revenue-icon.is-green { color: #55ae45; }
.gx-sb-detail__revenue-icon.is-orange { color: #ef8a25; }
.gx-sb-detail__revenue-icon.is-gray { color: #9aa1ad; }
.gx-sb-detail__link { color: #2674d8; text-decoration: underline; }
.gx-sb-detail__rating { display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 18px; padding: 0 4px; border-radius: 3px; font-size: 10px; }
.gx-sb-detail__rating.is-good { background: #e9f7e5; color: #45aa36; }
.gx-sb-detail__rating.is-normal { background: #e8f2ff; color: #347bd3; }
.gx-sb-detail__rating.is-bad { background: #fff0ef; color: #dc625d; }
.gx-sb-detail__suggestion {
  max-width: 760px;
  padding: 10px 12px;
  border: 2px solid rgb(224, 224, 236);
  border-left:3px solid var(--gx-red) ;
  border-radius: 6px;
  background: rgb(255, 245, 245);
}
.gx-sb-detail__suggestion-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.gx-sb-detail__suggestion-grid .gx-sb-detail__suggestion { max-width: none; }
.gx-sb-detail__suggestion strong {
  font-family: "Noto Sans SC", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--gx-black);
  margin-bottom: 4px;
}
.gx-sb-detail__suggestion p {
  font-family: "Noto Sans SC", sans-serif;
  font-size: 11px;
  color: rgb(102, 102, 102);
  margin-bottom: 6px;
}
.gx-sb-detail__suggestion ol {
  font-family: "Noto Sans SC", sans-serif;
  font-size: 11px;
  color: var(--gx-black);
  line-height: 1.7;
  margin:0 12px;
}
.gx-sb-detail__suggestion li {
  list-style: auto;
}
.gx-sb-detail__suggestion b {
  font-family: "Noto Sans SC", sans-serif;
  font-size: 11px;
  color: var(--gx-green);
  margin-top: 6px;
  font-weight: 600;
}
.gx-sb-detail__transaction-tabs { display: flex; overflow-x: auto; border-bottom: 1px solid #e4e7ed; }
.gx-sb-detail__transaction-tabs button { flex: 0 0 auto; height: 32px; padding: 0 15px; border: 0; border-bottom: 2px solid transparent; background: transparent; color: #798397; cursor: pointer; font-size: 11px; }
.gx-sb-detail__transaction-tabs button:hover, .gx-sb-detail__transaction-tabs button.is-active { border-bottom-color: var(--gx-red); color: var(--gx-red); font-weight: 600; }
.gx-sb-detail__subhead--transaction { color: #3e485c; font-weight: 600; }
.gx-sb-detail__transaction-box{
  padding: 16px 18px ;
}

.gx-sb-detail__refund-tabs button:hover, .gx-sb-detail__refund-tabs button.is-active { border-bottom-color: var(--gx-red); color:var(--gx-red); }
.gx-sb-detail__refund-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
  color: #727d8f;
  font-size: 11px;
}
.gx-sb-detail__refund-toolbar button { height: 24px; padding: 0 10px; border: 1px solid #e2e5eb; border-radius: 4px; background: #fff; color: #7a8495; cursor: pointer; font-size: 11px; }
.gx-sb-detail__refund-toolbar button:hover, .gx-sb-detail__refund-toolbar button.is-active { border-color: var(--gx-red); color:var(--gx-red); }
.gx-sb-detail__subhead--refund {display: flex; align-items: center; gap: 8px;  margin:0 0 14px;color:var(--gx-black);font-weight: 700; }
.gx-sb-detail__table--refund { min-width: 2320px; }
.gx-sb-detail__table--refund th { height: 34px; }
.gx-sb-detail__table tbody tr.is-empty td { height: 48px; color: #9aa1ad; text-align: center; }

@media (max-width: 900px) {
  .gx-sb-detail__panel-head { align-items: flex-start; flex-direction: column; gap: 8px; padding-top: 10px; padding-bottom: 9px; }
  .gx-sb-detail__panel-actions { width: 100%; overflow-x: auto; padding-bottom: 2px; }
  .gx-sb-detail__status-grid, .gx-sb-detail__average-grid { grid-template-columns: repeat(6, 150px); }
  .gx-sb-detail__group-summary-grid { grid-template-columns: repeat(6, 150px); }
  .gx-sb-detail__action-columns { grid-template-columns: minmax(150px, 1fr) 20px minmax(150px, 1fr); overflow-x: auto; }
  .gx-sb-detail__action-columns > div.is-positive { grid-column: 1 / -1; }
  .gx-sb-detail__suggestion-grid { grid-template-columns: 1fr; }
}
@media (max-width: 640px) {
  .gx-sb-detail__modebar { align-items: flex-start; flex-direction: column; padding-bottom: 9px; }
  .gx-sb-detail__scope { margin-top: 8px; margin-left: 0; padding-left: 0; border-left: 0; }
  .gx-sb-detail__content { padding: 10px; }
  .gx-sb-detail__scope select { width: 150px; }
  .gx-sb-detail__shop-multi-select { width: 180px; }
  .gx-sb-detail__group-multi-select { width: 150px; }
}
</style>
